import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SectionGlow from '@/components/common/SectionGlow';
import axios from 'axios';
import { Music, PlayCircle, Film, MessageSquare, User, Heart, MessageCircle, Mic2 } from 'lucide-react';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { trackClick } from '@/lib/track';
import { useJoinPrice } from '@/hooks/useJoinPrice';
import { CopyWithCoinPrice, COIN_PRICE_SLOT } from '@/components/common/CoinPrice';
import { SButton } from '@/components/common/SButton';
import { AvatarStack } from '@/components/common/AvatarStack';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_DEFS = [
  { Icon: Music, k: 'music', href: '/music' },
  { Icon: PlayCircle, k: 'videos', href: '/watch' },
  { Icon: Film, k: 'movies', href: '/watch' },
  { Icon: MessageSquare, k: 'community', href: '/community' },
  { Icon: User, k: 'profile', href: '/u/samtwo' },
  { Icon: Heart, k: 'saved', href: '/music' },
  { Icon: Mic2, k: 'lyrics', href: '/music' },
  { Icon: MessageCircle, k: 'stories', href: '/u/samtwo' },
];

// Urgency line pool (spec Section 7). Relocated to the Hero (ParticleHero.js).

const priceLabel = (p) => (p == null ? '' : Number.isInteger(p) ? `$${p}` : `$${p.toFixed(2)}`);

// Pick one item per page load, never the same as the previous load (localStorage-tracked).
const pickRotating = (arr, key) => {
  if (!arr || !arr.length) return '';
  if (arr.length === 1) return arr[0];
  let last = -1;
  try { last = parseInt(localStorage.getItem(key) ?? '-1', 10); } catch {}
  let i = Math.floor(Math.random() * arr.length);
  if (i === last) i = (i + 1) % arr.length;
  try { localStorage.setItem(key, String(i)); } catch {}
  return arr[i];
};

const FeatureCard = ({ Icon, k, href, t }) => (
  <a
    href={href}
    data-testid={`wym-card-${k}`}
    className="block rounded-xl p-4 cursor-pointer transition-shadow hover:shadow-[0_0_0_2px_rgba(37,117,252,0.2)]"
    style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)' }}
  >
    <Icon size={24} style={{ color: 'var(--accent-primary)' }} />
    <h3 className="mt-3 font-semibold" style={{ color: 'var(--text-primary)' }}>{t(`home.wym.${k}_t`)}</h3>
    <p className="mt-1" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{t(`home.wym.${k}_d`)}</p>
  </a>
);

const WhatYoureMissing = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const { everSubscribed } = usePlatformAuth();
  const navigate = useNavigate();
  const { joinPrice, joinCoins, resolveCopy } = useJoinPrice();
  const [pricing, setPricing] = useState(null);
  const [social, setSocial] = useState({ count: 0, avatars: [] });
  const [wym, setWym] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/pricing`, { params: { lang } }).then((r) => setPricing(r.data)).catch(() => {});
    axios.get(`${API}/live/subscribers/count`).then((r) => setSocial(r.data)).catch(() => {});
    axios.get(`${API}/home/what-youre-missing`, { params: { lang } }).then((r) => setWym(r.data)).catch(() => {});
  }, [lang]);

  const comparison = useMemo(
    () => pickRotating(pricing?.copy?.monthly, 'home_cmp_last'),
    [pricing]
  );

  // Non-subscribers only.
  if (everSubscribed) return null;

  const price = joinPrice || priceLabel(pricing?.plans?.monthly?.price);

  return (
    <section data-testid="what-youre-missing" className="relative overflow-hidden py-20 md:py-28" style={{ background: 'var(--bg-primary)' }}>
      <SectionGlow position="80% 10%" strength={10} />
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-h2 font-bold text-center"
          style={{ color: 'var(--text-primary)' }}
        >
          {resolveCopy(wym?.headline) || "Don't just follow. Become his boss."}
        </motion.h2>
        <p className="mt-4 text-center text-body-lg" style={{ color: 'var(--text-secondary)' }}>{t('home.wym.behind_door')}</p>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="wym-feature-grid">
          {FEATURE_DEFS.map((f) => <FeatureCard key={f.k} {...f} t={t} />)}
        </div>

        <p className="mt-10 text-center text-body-lg" style={{ color: 'var(--text-secondary)' }}>
          <CopyWithCoinPrice template={t('home.wym.front_of_door', { price: COIN_PRICE_SLOT })} coins={joinCoins} size="sm" />
        </p>

        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
          <SButton
            variant="primary"
            data-testid="wym-join-cta"
            onClick={() => { trackClick('subscribe_cta', { source: 'what_youre_missing' }); navigate('/pricing'); }}
            className="text-sm sm:text-base"
          >
            <CopyWithCoinPrice template={t('home.wym.join_for', { price: COIN_PRICE_SLOT })} coins={joinCoins} size="sm" />
          </SButton>
          <SButton
            variant="secondary"
            data-testid="wym-annual-cta"
            onClick={() => { trackClick('subscribe_cta', { source: 'what_youre_missing_annual' }); navigate('/pricing'); }}
            className="text-sm sm:text-base"
          >
            {t('home.wym.see_annual')}
          </SButton>
        </div>

        {comparison && price && (
          <p data-testid="wym-comparison" className="mt-4 text-center text-sm italic" style={{ color: 'var(--text-secondary)' }}>
            {comparison.replace(/\{\{\s*price\s*\}\}/g, price)}
          </p>
        )}
      </div>
    </section>
  );
};

export default WhatYoureMissing;
